/**
 * V37.69 — Page admin unique "Mandats SEPA" agregant RCDS + DOMIUM + LEGALCOOP.
 *
 * Affiche les mandats SEPA des 3 modules avec filtres scheme / status /
 * environnement / source, KPI agreges, et bouton "Tester le webhook" qui
 * envoie un event fictif signe HMAC vers /api/legalcoop/sepa/webhook pour
 * valider la chaine complete signature + idempotence + persistence.
 *
 * Reserve admin / super_admin.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Loader2, ArrowLeft, RefreshCw, Zap, ShieldCheck, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCHEME_META = {
  sepa_core: { label: 'CORE (B2C)', color: 'bg-blue-700 text-white border-blue-900 font-semibold' },
  sepa_b2b: { label: 'B2B', color: 'bg-purple-700 text-white border-purple-900 font-semibold' },
};

const STATUS_META = {
  active:                     { label: 'Actif', color: 'bg-emerald-700 text-white border-emerald-900 font-semibold' },
  pending:                    { label: 'En attente', color: 'bg-amber-600 text-white border-amber-800 font-semibold' },
  pending_customer_approval:  { label: 'Signature requise', color: 'bg-amber-600 text-white border-amber-800 font-semibold' },
  submitted:                  { label: 'Soumis', color: 'bg-cyan-700 text-white border-cyan-900 font-semibold' },
  cancelled:                  { label: 'Annulé', color: 'bg-red-700 text-white border-red-900 font-semibold' },
  failed:                     { label: 'Échec', color: 'bg-red-700 text-white border-red-900 font-semibold' },
  expired:                    { label: 'Expiré', color: 'bg-slate-600 text-white border-slate-800 font-semibold' },
};

const SOURCE_META = {
  RCDS:        { label: 'RCDS', color: 'bg-teal-700 text-white border-teal-900 font-semibold' },
  DOMIUM:      { label: 'DOMIUM', color: 'bg-fuchsia-700 text-white border-fuchsia-900 font-semibold' },
  RCDS_DOMIUM: { label: 'RCDS/DOMIUM', color: 'bg-indigo-700 text-white border-indigo-900 font-semibold' },
  LEGALCOOP:   { label: 'LEGALCOOP', color: 'bg-violet-700 text-white border-violet-900 font-semibold' },
};

const getAuthHeader = () => {
  const t = localStorage.getItem('admin_token')
         || localStorage.getItem('token')
         || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('fr-FR'); }
  catch { return String(iso).slice(0, 16); }
};

export default function SepaMandatesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [currentEnv, setCurrentEnv] = useState('');
  const [filterScheme, setFilterScheme] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEnv, setFilterEnv] = useState('all');
  const [filterSource, setFilterSource] = useState('all');

  const [webhookModal, setWebhookModal] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [webhookSubmitting, setWebhookSubmitting] = useState(false);
  const [webhookAction, setWebhookAction] = useState('active');
  const [webhookResource, setWebhookResource] = useState('mandates');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterScheme !== 'all') params.scheme = filterScheme;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterEnv !== 'all') params.environment = filterEnv;
      if (filterSource !== 'all') params.source = filterSource;
      const res = await axios.get(`${API}/super-admin/sepa/mandates`, {
        params, headers: getAuthHeader(),
      });
      setItems(res.data?.items || []);
      setCounters(res.data?.counters || {});
      setCurrentEnv(res.data?.current_environment || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement mandats');
    } finally {
      setLoading(false);
    }
  }, [filterScheme, filterStatus, filterEnv, filterSource]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const submitTestWebhook = async () => {
    setWebhookSubmitting(true); setWebhookResult(null);
    try {
      const res = await axios.post(`${API}/super-admin/sepa/test-webhook`, {
        resource_type: webhookResource,
        action: webhookAction,
        target_path: '/api/legalcoop/sepa/webhook',
      }, { headers: getAuthHeader() });
      setWebhookResult(res.data);
      if (res.data?.success) toast.success(`Webhook test envoyé (${res.data.event_id})`);
      else toast.error(`Webhook a répondu ${res.data?.response?.status_code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur test webhook');
      setWebhookResult({ error: err?.response?.data?.detail });
    } finally {
      setWebhookSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-4" data-testid="sepa-mandates-page">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin')} className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour Super Admin
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-violet-700" />
            Mandats SEPA — RCDS · DOMIUM · LEGALCOOP
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Vue agrégée des 3 modules. Environnement actuel : <b>{currentEnv || '—'}</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setWebhookModal(true); setWebhookResult(null); }}
            className="bg-violet-700 hover:bg-violet-800 text-white"
            data-testid="sepa-test-webhook-btn">
            <Zap className="h-4 w-4 mr-1" /> Tester le webhook
          </Button>
          <Button variant="outline" onClick={fetchList} disabled={loading} data-testid="sepa-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Actualiser
          </Button>
        </div>
      </div>

      {/* KPI Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sepa-kpi">
        <Card><CardContent className="pt-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Total mandats</div>
          <div className="text-2xl font-bold" data-testid="sepa-kpi-total">{items.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Actifs</div>
          <div className="text-2xl font-bold text-emerald-700">{counters.by_status?.active || 0}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">CORE / B2B</div>
          <div className="text-lg font-bold">{counters.by_scheme?.sepa_core || 0} / {counters.by_scheme?.sepa_b2b || 0}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">Par module</div>
          <div className="text-xs">
            RCDS: <b>{counters.by_source?.RCDS || counters.by_source?.RCDS_DOMIUM || 0}</b> ·
            DOMIUM: <b>{counters.by_source?.DOMIUM || 0}</b> ·
            LEGALCOOP: <b>{counters.by_source?.LEGALCOOP || 0}</b>
          </div>
        </CardContent></Card>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1"><Filter className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold">Filtres :</span></div>
            <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
              className="border rounded px-2 py-1 text-sm" data-testid="sepa-filter-source">
              <option value="all">Tous modules</option>
              <option value="RCDS_DOMIUM">RCDS / DOMIUM</option>
              <option value="LEGALCOOP">LEGALCOOP</option>
            </select>
            <select value={filterScheme} onChange={(e) => setFilterScheme(e.target.value)}
              className="border rounded px-2 py-1 text-sm" data-testid="sepa-filter-scheme">
              <option value="all">Tous schemes</option>
              <option value="sepa_core">CORE (B2C)</option>
              <option value="sepa_b2b">B2B</option>
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded px-2 py-1 text-sm" data-testid="sepa-filter-status">
              <option value="all">Tous statuts</option>
              <option value="active">Actif</option>
              <option value="pending">En attente</option>
              <option value="pending_customer_approval">Signature requise</option>
              <option value="submitted">Soumis</option>
              <option value="cancelled">Annulé</option>
              <option value="failed">Échec</option>
              <option value="expired">Expiré</option>
            </select>
            <select value={filterEnv} onChange={(e) => setFilterEnv(e.target.value)}
              className="border rounded px-2 py-1 text-sm" data-testid="sepa-filter-env">
              <option value="all">Tous environnements</option>
              <option value="sandbox">Sandbox</option>
              <option value="live">Live</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mandats ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate-500 italic">Aucun mandat à afficher avec ces filtres.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sepa-mandates-table">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-2 text-left">Module</th>
                    <th className="px-2 py-2 text-left">Scheme</th>
                    <th className="px-2 py-2 text-left">Statut</th>
                    <th className="px-2 py-2 text-left">Env.</th>
                    <th className="px-2 py-2 text-left">Client</th>
                    <th className="px-2 py-2 text-left">Mandate ID</th>
                    <th className="px-2 py-2 text-left">Référence</th>
                    <th className="px-2 py-2 text-right">Créé le</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => {
                    const srcMeta = SOURCE_META[m.source] || SOURCE_META.RCDS_DOMIUM;
                    const stMeta = STATUS_META[m.status] || STATUS_META.pending;
                    const scMeta = SCHEME_META[m.scheme] || SCHEME_META.sepa_core;
                    return (
                      <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50"
                          data-testid={`sepa-mandate-row-${m.id}`}>
                        <td className="px-2 py-1.5"><Badge className={srcMeta.color}>{srcMeta.label}</Badge></td>
                        <td className="px-2 py-1.5"><Badge className={`${scMeta.color} text-[10px]`}>{scMeta.label}</Badge></td>
                        <td className="px-2 py-1.5"><Badge className={`${stMeta.color} text-[10px]`}>{stMeta.label}</Badge></td>
                        <td className="px-2 py-1.5 text-xs text-slate-600">{m.environment || '—'}</td>
                        <td className="px-2 py-1.5 text-xs">{m.customer_name || m.customer_email || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{m.mandate_id ? m.mandate_id.slice(0, 16) + '...' : '—'}</td>
                        <td className="px-2 py-1.5 text-[11px] text-slate-600">{m.reference || '—'}</td>
                        <td className="px-2 py-1.5 text-[11px] text-right text-slate-600">{fmtDate(m.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Test Webhook */}
      <Dialog open={webhookModal} onOpenChange={(o) => { if (!o) { setWebhookModal(false); setWebhookResult(null); } }}>
        <DialogContent data-testid="sepa-test-webhook-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-violet-700" />
              Tester le webhook GoCardless
            </DialogTitle>
            <DialogDescription>
              Envoie un event fictif <b>signé HMAC SHA-256</b> avec votre clé webhook
              vers <code className="text-xs">/api/legalcoop/sepa/webhook</code>. Valide la chaîne
              complète : signature + parsing + idempotence + persistence dans <code>gocardless_events</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Type de ressource</label>
              <select value={webhookResource} onChange={(e) => setWebhookResource(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
                data-testid="sepa-test-webhook-resource">
                <option value="mandates">mandates</option>
                <option value="payments">payments</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Action</label>
              <select value={webhookAction} onChange={(e) => setWebhookAction(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
                data-testid="sepa-test-webhook-action">
                <option value="active">active</option>
                <option value="cancelled">cancelled</option>
                <option value="failed">failed</option>
                <option value="confirmed">confirmed</option>
                <option value="submitted">submitted</option>
              </select>
            </div>
            {webhookResult && (
              <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs font-mono"
                   data-testid="sepa-test-webhook-result">
                <div><b>Event ID :</b> {webhookResult.event_id}</div>
                <div><b>Status :</b> {webhookResult.response?.status_code} — <i>{webhookResult.interpretation}</i></div>
                <div className="mt-1 text-[10px] text-slate-600">Body : {webhookResult.response?.body?.slice(0, 200)}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWebhookModal(false)}>Fermer</Button>
            <Button onClick={submitTestWebhook} disabled={webhookSubmitting}
              className="bg-violet-700 hover:bg-violet-800 text-white"
              data-testid="sepa-test-webhook-submit">
              {webhookSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />}
              Envoyer un event de test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
